import { useState, useCallback, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Search, Users, RefreshCw, Snowflake, Play, ArrowUpDown, ArrowUp, ArrowDown, Eye, FileText, Download, User, Building, AlertTriangle, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { getCustomerList, getCustomerDetail, toggleCustomerCreditFreeze, getCustomerFilterOptions } from '@/src/lib/api/admin-analytics'
import type { AdminCustomerListResponse, AdminCustomerRow, AdminCustomerDetail } from '@/src/types/admin/analytics'
import type { PackageFilterOption } from '@/src/lib/api/admin-analytics'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

// ─── Sort Types ───────────────────────────────────────────────────────────────

type SortField = 'email' | 'phone' | 'package_code' | 'kyc_status' | 'credit_balance' | 'created_at' | 'last_login_at' | 'is_active'
type SortDirection = 'asc' | 'desc'

interface SortConfig {
  field: SortField | null
  direction: SortDirection
}

export default function AdminUsers() {
  const [customers, setCustomers] = useState<AdminCustomerListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [packageFilter, setPackageFilter] = useState('')
  const [kycFilter, setKycFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: null, direction: 'asc' })
  // Freeze dialog state
  const [freezeTarget, setFreezeTarget] = useState<{ id: string; email: string; freeze: boolean } | null>(null)
  const [freezeLoading, setFreezeLoading] = useState(false)
  // Detail dialog state
  const [detailCustomer, setDetailCustomer] = useState<AdminCustomerDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  // KYC action state (approve/reject within detail dialog)
  const [kycActionLoading, setKycActionLoading] = useState<'approve' | 'reject' | null>(null)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  // Package filter options from database
  const [packageOptions, setPackageOptions] = useState<PackageFilterOption[]>([])

  useEffect(() => {
    getCustomerFilterOptions()
      .then(setPackageOptions)
      .catch(() => setPackageOptions([]))
  }, [])

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getCustomerList({
        page,
        limit: 20,
        search: search || undefined,
        package_code: packageFilter || undefined,
        kyc_status: kycFilter || undefined,
        is_active: statusFilter === '' ? undefined : statusFilter === 'active',
      })
      setCustomers(data)
    } finally {
      setLoading(false)
    }
  }, [page, search, packageFilter, kycFilter, statusFilter])

  useEffect(() => {
    fetchCustomers()
  }, [fetchCustomers])

  // ─── Sorting Logic ────────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' }
        return { field: null, direction: 'asc' }
      }
      return { field, direction: 'asc' }
    })
  }

  const sortedData = useMemo(() => {
    if (!customers?.data || !sortConfig.field) return customers?.data ?? []
    const { field, direction } = sortConfig
    return [...customers.data].sort((a, b) => {
      let aVal: string | number | boolean | undefined
      let bVal: string | number | boolean | undefined

      switch (field) {
        case 'email':
          aVal = a.email.toLowerCase()
          bVal = b.email.toLowerCase()
          break
        case 'phone':
          aVal = a.phone ?? ''
          bVal = b.phone ?? ''
          break
        case 'package_code':
          aVal = a.package_code
          bVal = b.package_code
          break
        case 'kyc_status':
          aVal = a.kyc_status ?? ''
          bVal = b.kyc_status ?? ''
          break
        case 'credit_balance':
          aVal = a.credit_balance
          bVal = b.credit_balance
          break
        case 'created_at':
          aVal = a.created_at
          bVal = b.created_at
          break
        case 'last_login_at':
          aVal = a.last_login_at ?? ''
          bVal = b.last_login_at ?? ''
          break
        case 'is_active':
          aVal = a.is_active ? 1 : 0
          bVal = b.is_active ? 1 : 0
          break
        default:
          return 0
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    })
  }, [customers?.data, sortConfig])

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortConfig.field !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1 text-primary" />
    return <ArrowDown className="h-3 w-3 ml-1 text-primary" />
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchCustomers()
  }

  const handleOpenDetail = async (customer: AdminCustomerRow) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailCustomer(null)
    try {
      const data = await getCustomerDetail(customer.customer_id)
      setDetailCustomer(data)
    } catch {
      toast.error('ไม่สามารถดึงข้อมูลลูกค้าได้')
      setDetailOpen(false)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleConfirmFreeze = async () => {
    if (!freezeTarget) return
    setFreezeLoading(true)
    try {
      await toggleCustomerCreditFreeze(freezeTarget.id, freezeTarget.freeze)
      setCustomers(prev =>
        prev
          ? {
              ...prev,
              data: prev.data.map(c =>
                c.customer_id === freezeTarget.id
                  ? { ...c, credit_usage_frozen: freezeTarget.freeze }
                  : c
              ),
            }
          : prev
      )
      toast.success(freezeTarget.freeze ? 'ระงับการใช้เครดิตเรียบร้อยแล้ว' : 'ปลดระงับการใช้เครดิตเรียบร้อยแล้ว')
    } catch {
      toast.error('เกิดข้อผิดพลาด ไม่สามารถเปลี่ยนสถานะเครดิตได้ กรุณาลองใหม่')
    } finally {
      setFreezeLoading(false)
      setFreezeTarget(null)
    }
  }

  // ─── KYC approve/reject (for pending customers) ───────────────────────────────

  const refreshDetailAndList = async (customerId: string) => {
    try {
      const data = await getCustomerDetail(customerId)
      setDetailCustomer(data)
    } catch {
      // Ignore refresh failure; list refresh below still updates the table
    }
    fetchCustomers()
  }

  const handleKycApprove = async () => {
    const verification = detailCustomer?.kyc_verification
    if (!detailCustomer || !verification) return

    setKycActionLoading('approve')
    try {
      const token = localStorage.getItem('authToken')
      const endpoint = verification.verification_type === 'individual'
        ? '/api/admin/kyc/individual/approve'
        : '/api/admin/kyc/company/approve'

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ verificationId: verification.id }),
      })

      if (!response.ok) throw new Error('Failed to approve verification')

      toast.success('อนุมัติการยืนยันตัวตนสำเร็จ')
      await refreshDetailAndList(detailCustomer.customer_id)
    } catch (error) {
      console.error('Error approving verification:', error)
      toast.error('เกิดข้อผิดพลาดในการอนุมัติ')
    } finally {
      setKycActionLoading(null)
    }
  }

  const handleKycReject = async () => {
    const verification = detailCustomer?.kyc_verification
    if (!detailCustomer || !verification || !rejectionReason.trim()) return

    setKycActionLoading('reject')
    try {
      const token = localStorage.getItem('authToken')
      const endpoint = verification.verification_type === 'individual'
        ? '/api/admin/kyc/individual/reject'
        : '/api/admin/kyc/company/reject'

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verificationId: verification.id,
          rejectionReason: rejectionReason.trim(),
        }),
      })

      if (!response.ok) throw new Error('Failed to reject verification')

      toast.success('ปฏิเสธการยืนยันตัวตนสำเร็จ')
      setShowRejectDialog(false)
      setRejectionReason('')
      await refreshDetailAndList(detailCustomer.customer_id)
    } catch (error) {
      console.error('Error rejecting verification:', error)
      toast.error('เกิดข้อผิดพลาดในการปฏิเสธ')
    } finally {
      setKycActionLoading(null)
    }
  }

  // ─── Document helpers ─────────────────────────────────────────────────────────

  const getDocumentTypeName = (docType: string): string => {
    const typeMap: Record<string, string> = {
      'id_card_front': 'บัตรประชาชน (ด้านหน้า)',
      'id_card_back': 'บัตรประชาชน (ด้านหลัง)',
      'house_registration': 'ทะเบียนบ้าน',
      'company_registration': 'หนังสือจดทะเบียนบริษัท',
      'director_id_card': 'บัตรประชาชนกรรมการ',
      'company_affidavit': 'หนังสือรับรองบริษัท',
      'vat_registration': 'ทะเบียนภาษีมูลค่าเพิ่ม (ภ.ภ.20)',
      'additional_document': 'เอกสารเพิ่มเติม',
      'bank_statement': 'สำเนาบัญชีธนาคาร',
      'power_of_attorney': 'หนังสือมอบอำนาจ',
    }
    return typeMap[docType] || docType
  }

  const handleDocumentView = (docId: string) => {
    const token = localStorage.getItem('authToken')
    const viewUrl = `${API_BASE}/api/admin/kyc/documents/view/${docId}?token=${encodeURIComponent(token || '')}`
    window.open(viewUrl, '_blank')
  }

  const handleDocumentDownload = (docId: string, docType: string) => {
    const token = localStorage.getItem('authToken')
    const downloadUrl = `${API_BASE}/api/admin/kyc/documents/download/${docId}?token=${encodeURIComponent(token || '')}`
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${docType}_${docId}`
    link.click()
  }

  // ─── Badge helpers ────────────────────────────────────────────────────────────

  const kycBadgeClass = (status?: string) => {
    if (status === 'approved') return 'border-green-400 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 dark:border-green-700'
    if (status === 'rejected') return 'border-red-400 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 dark:border-red-700'
    if (status === 'pending')  return 'border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-700'
    return 'border-muted-foreground/30 text-muted-foreground'
  }

  const kycLabel = (status?: string) => {
    if (status === 'approved') return 'อนุมัติ'
    if (status === 'rejected') return 'ปฏิเสธ'
    if (status === 'pending') return 'รอตรวจ'
    return '—'
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString))
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">จัดการผู้ใช้</h1>
        <p className="text-muted-foreground">จัดการข้อมูลลูกค้าและสถานะการใช้งาน</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            รายการลูกค้าทั้งหมด
            {customers && (
              <span className="ml-auto text-sm font-normal text-muted-foreground">
                {customers.pagination.total.toLocaleString()} รายการ
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหาอีเมล / เบอร์โทร / ชื่อธุรกิจ..."
                className="pl-8"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Select value={packageFilter} onValueChange={v => { setPackageFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="แพ็กเกจ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกแพ็กเกจ</SelectItem>
                {packageOptions.map(pkg => (
                  <SelectItem key={pkg.value} value={pkg.value}>{pkg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kycFilter} onValueChange={v => { setKycFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="KYC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุก KYC</SelectItem>
                <SelectItem value="pending">รอดำเนินการ</SelectItem>
                <SelectItem value="approved">อนุมัติแล้ว</SelectItem>
                <SelectItem value="rejected">ปฏิเสธ</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="สถานะ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกสถานะ</SelectItem>
                <SelectItem value="active">ใช้งาน</SelectItem>
                <SelectItem value="inactive">ระงับ</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="sm">ค้นหา</Button>
            <Button type="button" variant="outline" size="sm" onClick={fetchCustomers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </form>

          {/* Table */}
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('email')}>
                        ลูกค้า <SortIcon field="email" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('phone')}>
                        เบอร์โทร <SortIcon field="phone" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('package_code')}>
                        แพ็กเกจ <SortIcon field="package_code" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('kyc_status')}>
                        KYC <SortIcon field="kyc_status" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button type="button" className="flex items-center ml-auto hover:text-primary transition-colors" onClick={() => handleSort('credit_balance')}>
                        เครดิต <SortIcon field="credit_balance" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('created_at')}>
                        สมัครเมื่อ <SortIcon field="created_at" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('last_login_at')}>
                        เข้าใช้ล่าสุด <SortIcon field="last_login_at" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort('is_active')}>
                        สถานะ <SortIcon field="is_active" />
                      </button>
                    </TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                        ไม่พบข้อมูลลูกค้า
                      </TableCell>
                    </TableRow>
                  ) : sortedData.map(c => (
                    <TableRow
                      key={c.customer_id}
                      className={`cursor-pointer hover:bg-muted/50 ${!c.is_active ? 'opacity-60' : c.credit_usage_frozen ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
                      onClick={() => handleOpenDetail(c)}
                    >
                      <TableCell>
                        <div className="text-sm font-medium truncate max-w-[180px]">{c.email}</div>
                        {c.business_name && (
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {c.business_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.phone || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{c.package_code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${kycBadgeClass(c.kyc_status)}`}>
                          {kycLabel(c.kyc_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {c.credit_balance.toLocaleString('th-TH')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString('th-TH')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {c.last_login_at ? new Date(c.last_login_at).toLocaleDateString('th-TH') : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={c.is_active ? 'secondary' : 'outline'} className="text-xs w-fit">
                            {c.is_active ? 'ใช้งาน' : 'ระงับ'}
                          </Badge>
                          {c.credit_usage_frozen && (
                            <Badge variant="outline" className="text-xs w-fit border-blue-400 text-blue-600 dark:text-blue-400">
                              <Snowflake className="h-3 w-3 mr-1" />
                              ระงับเครดิต
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleOpenDetail(c) }}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            ดูข้อมูล
                          </Button>
                          <Button
                            variant={c.credit_usage_frozen ? 'outline' : 'ghost'}
                            size="sm"
                            className={c.credit_usage_frozen
                              ? 'text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-950 h-7 px-2 text-xs'
                              : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 h-7 px-2 text-xs'
                            }
                            onClick={(e) => { e.stopPropagation(); setFreezeTarget({ id: c.customer_id, email: c.email, freeze: !c.credit_usage_frozen }) }}
                          >
                            {c.credit_usage_frozen
                              ? <><Play className="h-3 w-3 mr-1" />ปลดระงับ</>
                              : <><Snowflake className="h-3 w-3 mr-1" />ระงับเครดิต</>
                            }
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {customers && customers.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{customers.pagination.total.toLocaleString()} รายการ</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      ก่อนหน้า
                    </Button>
                    <span className="px-2">{page} / {customers.pagination.total_pages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(customers.pagination.total_pages, p + 1))}
                      disabled={page === customers.pagination.total_pages}
                    >
                      ถัดไป
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ─── Customer Detail Dialog ──────────────────────────────────────────────── */}
      <Dialog open={detailOpen} onOpenChange={(open) => { if (!open) { setDetailOpen(false); setDetailCustomer(null) } }}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              รายละเอียดลูกค้า
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="space-y-4 py-6">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : detailCustomer ? (
            <div className="space-y-5">
              {/* ─── Basic Info ─────────────────────────────────────────────────────── */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">📞</span>
                    </div>
                    ข้อมูลติดต่อ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">อีเมล</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border break-all">{detailCustomer.email}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">เบอร์โทรศัพท์</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border">{detailCustomer.phone || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">ชื่อธุรกิจ</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border">{detailCustomer.business_name || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">แพ็กเกจ</Label>
                      <div className="pt-1">
                        <Badge variant="outline">{detailCustomer.package_code}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ─── Account Status ────────────────────────────────────────────────── */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 text-xs">📅</span>
                    </div>
                    สถานะบัญชี
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">สถานะ</Label>
                      <div className="pt-0.5">
                        <Badge variant={detailCustomer.is_active ? 'secondary' : 'outline'}>
                          {detailCustomer.is_active ? 'ใช้งาน' : 'ระงับ'}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">สถานะเครดิต</Label>
                      <div className="pt-0.5">
                        {detailCustomer.credit_usage_frozen ? (
                          <Badge variant="outline" className="border-blue-400 text-blue-600 dark:text-blue-400">
                            <Snowflake className="h-3 w-3 mr-1" />ระงับ
                          </Badge>
                        ) : (
                          <Badge variant="secondary">ปกติ</Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">เครดิตคงเหลือ</Label>
                      <p className="text-sm font-semibold pt-0.5">{detailCustomer.credit_balance.toLocaleString('th-TH')}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">วันที่สมัคร</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border">{formatDate(detailCustomer.created_at)}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">เข้าใช้งานล่าสุด</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border">{detailCustomer.last_login_at ? formatDate(detailCustomer.last_login_at) : '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">วันหมดอายุแพ็กเกจ</Label>
                      <p className="text-sm bg-muted/50 p-2.5 rounded border">{detailCustomer.package_expiry_date ? formatDate(detailCustomer.package_expiry_date) : '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ─── KYC Verification ──────────────────────────────────────────────── */}
              {detailCustomer.kyc_verification ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        {detailCustomer.kyc_verification.verification_type === 'individual'
                          ? <User className="w-3 h-3 text-blue-600" />
                          : <Building className="w-3 h-3 text-blue-600" />
                        }
                      </div>
                      ข้อมูลการยืนยันตัวตน
                      <Badge variant="outline" className={`ml-2 text-xs ${kycBadgeClass(detailCustomer.kyc_verification.status)}`}>
                        {kycLabel(detailCustomer.kyc_verification.status)}
                      </Badge>
                      <Badge variant="outline" className="text-xs ml-1">
                        {detailCustomer.kyc_verification.verification_type === 'individual' ? 'บุคคลธรรมดา' : 'นิติบุคคล'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {detailCustomer.kyc_verification.verification_type === 'individual' ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">คำนำหน้า</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.prefix || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">ชื่อ</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.firstName || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">นามสกุล</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.lastName || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">เลขบัตรประชาชน</Label>
                          <p className="text-sm bg-muted/50 p-2.5 rounded border font-mono">
                            {detailCustomer.kyc_verification.verification_data.idCard || '—'}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">ที่อยู่</Label>
                          <p className="text-sm bg-muted/50 p-2.5 rounded border">
                            {detailCustomer.kyc_verification.verification_data.address || '—'}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">จังหวัด</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.province || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">เขต/อำเภอ</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.subDistrict || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">แขวง/ตำบล</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.district || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">รหัสไปรษณีย์</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border font-mono">
                              {detailCustomer.kyc_verification.verification_data.postalCode || '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">ชื่อบริษัท</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.companyName || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">ประเภทธุรกิจ</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.businessType || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">เลขประจำตัวผู้เสียภาษี</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border font-mono">
                              {detailCustomer.kyc_verification.verification_data.taxId || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">ผู้ติดต่อ</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.contactPerson || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">ที่อยู่</Label>
                          <p className="text-sm bg-muted/50 p-2.5 rounded border">
                            {detailCustomer.kyc_verification.verification_data.address || '—'}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">จังหวัด</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.province || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">เขต/อำเภอ</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.subDistrict || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">แขวง/ตำบล</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border">
                              {detailCustomer.kyc_verification.verification_data.district || '—'}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">รหัสไปรษณีย์</Label>
                            <p className="text-xs bg-muted/50 p-2 rounded border font-mono">
                              {detailCustomer.kyc_verification.verification_data.postalCode || '—'}
                            </p>
                          </div>
                        </div>
                        {detailCustomer.kyc_verification.verification_data.branch && (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">สาขา</Label>
                            <p className="text-sm bg-muted/50 p-2.5 rounded border">
                              {detailCustomer.kyc_verification.verification_data.branch}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* KYC Timeline */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">ส่งเมื่อ</Label>
                        <p className="text-xs bg-muted/50 p-2 rounded border">
                          {formatDate(detailCustomer.kyc_verification.submitted_at)}
                        </p>
                      </div>
                      {detailCustomer.kyc_verification.verified_at && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">อนุมัติเมื่อ</Label>
                          <p className="text-xs bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400">
                            {formatDate(detailCustomer.kyc_verification.verified_at)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Rejection reason */}
                    {detailCustomer.kyc_verification.status === 'rejected' && detailCustomer.kyc_verification.rejection_reason && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-950/30 rounded border border-red-200 dark:border-red-800">
                        <div className="flex items-center gap-1 text-red-700 dark:text-red-400 text-xs font-medium mb-1">
                          <AlertTriangle className="h-3 w-3" />
                          เหตุผลการปฏิเสธ
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-300">
                          {detailCustomer.kyc_verification.rejection_reason}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-6 text-center text-muted-foreground">
                    <User className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">ยังไม่ได้ยืนยันตัวตน (KYC)</p>
                  </CardContent>
                </Card>
              )}

              {/* ─── KYC Documents ─────────────────────────────────────────────────── */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                      <FileText className="w-3 h-3 text-purple-600" />
                    </div>
                    เอกสารแนบ ({detailCustomer.kyc_documents.length} ไฟล์)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {detailCustomer.kyc_documents.length > 0 ? (
                    <div className="space-y-2">
                      {detailCustomer.kyc_documents.map((doc) => (
                        <div key={doc.id} className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                              <div>
                                <p className="text-sm font-medium">{getDocumentTypeName(doc.document_type)}</p>
                                <p className="text-xs text-muted-foreground">
                                  อัปโหลดเมื่อ {formatDate(doc.uploaded_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => handleDocumentView(doc.id)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                ดู
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => handleDocumentDownload(doc.id, doc.document_type)}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                ดาวน์โหลด
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">ไม่มีเอกสารแนบ</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ─── Actions ───────────────────────────────────────────────────────── */}
              <div className="flex flex-wrap gap-2 justify-end pt-2">
                {/* KYC approve/reject — only for new customers awaiting verification */}
                {detailCustomer.kyc_verification?.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={handleKycApprove}
                      disabled={!!kycActionLoading}
                    >
                      {kycActionLoading === 'approve' ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent mr-1" />
                      ) : (
                        <Check className="h-3 w-3 mr-1" />
                      )}
                      อนุมัติ
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => setShowRejectDialog(true)}
                      disabled={!!kycActionLoading}
                    >
                      <X className="h-3 w-3 mr-1" />
                      ปฏิเสธ
                    </Button>
                  </>
                )}
                <Button
                  variant={detailCustomer.credit_usage_frozen ? 'outline' : 'ghost'}
                  size="sm"
                  className={detailCustomer.credit_usage_frozen
                    ? 'text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-950'
                    : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950'
                  }
                  onClick={() => {
                    setFreezeTarget({ id: detailCustomer.customer_id, email: detailCustomer.email, freeze: !detailCustomer.credit_usage_frozen })
                    setDetailOpen(false)
                    setDetailCustomer(null)
                  }}
                >
                  {detailCustomer.credit_usage_frozen
                    ? <><Play className="h-3 w-3 mr-1" />ปลดระงับเครดิต</>
                    : <><Snowflake className="h-3 w-3 mr-1" />ระงับเครดิต</>
                  }
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* KYC Reject reason dialog */}
      <Dialog open={showRejectDialog} onOpenChange={(open) => { if (!open) { setShowRejectDialog(false); setRejectionReason('') } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ปฏิเสธการยืนยันตัวตน</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="kyc-reject-reason" className="text-sm font-medium">
                เหตุผลในการปฏิเสธ *
              </Label>
              <Textarea
                id="kyc-reject-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="กรุณาระบุเหตุผลในการปฏิเสธ..."
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowRejectDialog(false); setRejectionReason('') }}
              disabled={!!kycActionLoading}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleKycReject}
              disabled={!rejectionReason.trim() || !!kycActionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {kycActionLoading === 'reject' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
              ) : null}
              ปฏิเสธ
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Freeze/Unfreeze confirmation dialog */}
      <AlertDialog open={!!freezeTarget} onOpenChange={(open) => { if (!open) setFreezeTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {freezeTarget?.freeze ? 'ระงับการใช้เครดิต' : 'ปลดระงับการใช้เครดิต'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {freezeTarget?.freeze
                ? `ลูกค้า "${freezeTarget?.email}" จะไม่สามารถใช้เครดิตเพื่อเรียก API ได้จนกว่าจะปลดระงับ แต่ยังสามารถดูประวัติและข้อมูลบัญชีได้ตามปกติ`
                : `ลูกค้า "${freezeTarget?.email}" จะกลับมาใช้เครดิต API ได้ตามปกติ`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={freezeLoading}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmFreeze}
              disabled={freezeLoading}
              className={freezeTarget?.freeze
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
              }
            >
              {freezeLoading ? 'กำลังดำเนินการ...' : (freezeTarget?.freeze ? 'ยืนยันระงับเครดิต' : 'ยืนยันปลดระงับ')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
